import React from "react";
import { Button, Card, Divider, Flex, Progress, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import CoursesTable, { MONO } from "./CoursesTable";
import type { DownloadCourse, DownloadParts } from "./downloads.types";
import { isSettled } from "./rowResult";
import type { DownloadRowState } from "./rowResult";
import type { RunPhase } from "./useDownloadRun";

const { Text, Link } = Typography;

const BAR_PADDING: React.CSSProperties = { padding: "16px 24px" };

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

// An environment without the summary's folder or tracker has nothing to link.
const Destination: React.FC<{ href: string | null; label: string }> = ({
  href,
  label,
}) =>
  href ? (
    <Link href={href} target="_blank">
      {label}
    </Link>
  ) : (
    <>{label}</>
  );

interface Props {
  courses: DownloadCourse[];
  rows: Record<number, DownloadRowState>;
  parts: DownloadParts;
  selectedIds: number[];
  phase: RunPhase;
  folderUrl: string | null;
  trackerUrl: string | null;
  onSelect: (ids: number[]) => void;
  onDownload: () => void;
}

const CoursesCard: React.FC<Props> = ({
  courses,
  rows,
  parts,
  selectedIds,
  phase,
  folderUrl,
  trackerUrl,
  onSelect,
  onDownload,
}) => {
  const marked = selectedIds.length;
  const settled = Object.values(rows).filter(isSettled).length;
  const canDownload =
    marked > 0 &&
    (parts.od || parts.quiz || parts.quizVideo || parts.odSummary);

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <Flex
        justify="space-between"
        align="center"
        wrap
        gap={12}
        style={BAR_PADDING}
      >
        <Text strong style={{ fontSize: 17 }}>
          {`${plural(courses.length, "curso", "cursos")} · ${plural(marked, "marcado", "marcados")}`}
        </Text>
        <Text type="secondary">
          Se guardan en la carpeta{" "}
          <Destination
            href={folderUrl}
            label={parts.odSummary ? "HUB · OD resumidas" : "HUB · Descargas"}
          />{" "}
          y se registran en el{" "}
          <Destination
            href={trackerUrl}
            label={
              parts.odSummary
                ? "tracker de ODs resumidas"
                : "tracker de descargas"
            }
          />
        </Text>
      </Flex>
      <CoursesTable
        courses={courses}
        rows={rows}
        parts={parts}
        selectedIds={selectedIds}
        locked={phase !== "idle"}
        onSelect={onSelect}
      />
      {phase === "idle" || phase === "starting" ? (
        <>
          <Divider style={{ margin: 0 }} />
          <Flex
            justify="space-between"
            align="center"
            wrap
            gap={12}
            style={BAR_PADDING}
          >
            <Text type="secondary">
              {parts.odSummary ? (
                <>
                  Se guarda en Grado / Asignatura / T#: la OD resumida (
                  <span style={MONO}>CLAVE_OD_RESUMIDA_FE</span>) y sus carpetas
                  de imágenes (
                  <span style={MONO}>CLAVE_OD_RESUMIDA_FE_imagenes</span>) y
                  descargables (
                  <span style={MONO}>CLAVE_OD_RESUMIDA_FE_descargables</span>).
                  Lo ya descargado se sobrescribe en el mismo enlace.
                </>
              ) : (
                <>
                  Se guarda en Grado / Asignatura / T# / CLAVE_Título: la OD (
                  <span style={MONO}>CLAVE_Título_OD_FE</span>), el quiz (
                  <span style={MONO}>CLAVE_Título_QUIZ_FE.yml</span>), el quiz
                  de video (
                  <span style={MONO}>CLAVE_Título_QUIZ_VIDEO_FE.yml</span>), las
                  notas de revisión (
                  <span style={MONO}>CLAVE_Título_NOTAS_REVISION</span>) y las
                  carpetas de imágenes y descargables. Lo ya descargado se
                  sobrescribe en el mismo enlace.
                </>
              )}
            </Text>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={phase === "starting"}
              disabled={!canDownload}
              onClick={onDownload}
            >
              {`Descargar (${marked})`}
            </Button>
          </Flex>
        </>
      ) : null}
      {phase === "running" ? (
        <>
          <Divider style={{ margin: 0 }} />
          <Flex align="center" wrap gap={16} style={BAR_PADDING}>
            <Progress
              percent={Math.round((settled / courses.length) * 100)}
              showInfo={false}
              style={{ flex: 1, minWidth: 200, margin: 0 }}
            />
            <Text type="secondary" aria-live="polite">
              {`${settled} de ${courses.length} · escribiendo en Drive y en el tracker`}
            </Text>
          </Flex>
        </>
      ) : null}
    </Card>
  );
};

export default CoursesCard;
